import PropTypes from 'prop-types';

import { Box, Card, Stack, Typography } from '@mui/material';

export const adminTokens = {
  bg: '#F5F7F4',
  surface: '#FFFFFF',
  surfaceAlt: '#F0F5F2',
  ink: '#18221F',
  muted: '#66736E',
  accent: '#147D72',
  accentHover: '#106A61',
  border: '#DDE4E0',
  borderStrong: '#BFC9C4',
  hover: '#EEF5F2'
};

export const adminPrimaryButtonSx = {
  bgcolor: adminTokens.accent,
  color: '#FFFFFF',
  boxShadow: 'none',
  borderRadius: '4px',
  minHeight: 40,
  fontWeight: 650,
  textTransform: 'none',
  '&:hover': { bgcolor: adminTokens.accentHover, boxShadow: 'none' }
};

export const adminSecondaryButtonSx = {
  color: '#30413B',
  borderColor: adminTokens.borderStrong,
  borderRadius: '4px',
  minHeight: 38,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { borderColor: adminTokens.accent, bgcolor: adminTokens.hover }
};

export const adminToolbarSx = {
  minHeight: 58,
  height: 'auto',
  px: { xs: 2, sm: 3 },
  py: 1.5,
  display: 'flex',
  justifyContent: 'flex-end',
  borderTop: `1px solid ${adminTokens.border}`,
  '& .MuiButtonGroup-root': {
    flexWrap: 'wrap',
    gap: 1,
    boxShadow: 'none',
    mb: '0 !important'
  },
  '& .MuiButtonGroup-grouped': {
    borderRadius: '4px !important',
    border: `1px solid ${adminTokens.borderStrong} !important`,
    color: adminTokens.accent,
    minHeight: 36,
    px: 1.5,
    mx: '0 !important',
    '&:hover': {
      borderColor: `${adminTokens.accent} !important`,
      bgcolor: adminTokens.hover
    }
  }
};

export function AdminPage({ children, spacing = 2.5, sx }) {
  return (
    <Stack
      className="zhilv-admin-page"
      spacing={spacing}
      sx={{
        minHeight: 'calc(100vh - 72px)',
        bgcolor: adminTokens.bg,
        color: adminTokens.ink,
        p: { xs: 2, sm: 3, md: 3.5, lg: 4 },
        overflowX: 'hidden',
        '& .MuiCard-root': {
          border: `1px solid ${adminTokens.border}`,
          borderRadius: '6px',
          boxShadow: 'none',
          backgroundImage: 'none'
        },
        '& .MuiButton-root': {
          textTransform: 'none'
        },
        '& .MuiOutlinedInput-root': {
          borderRadius: '4px',
          bgcolor: adminTokens.surface,
          '& fieldset': { borderColor: adminTokens.borderStrong },
          '&:hover fieldset': { borderColor: adminTokens.accent },
          '&.Mui-focused fieldset': { borderColor: adminTokens.accent, borderWidth: 1 }
        },
        ...sx
      }}
    >
      {children}
    </Stack>
  );
}

export function AdminPageHeader({ title, description, actions, eyebrow, actionAlign = 'flex-end' }) {
  return (
    <Box sx={{ pb: 2.5, borderBottom: `1px solid ${adminTokens.border}` }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: actionAlign }} justifyContent="space-between" spacing={2.5}>
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography sx={{ color: adminTokens.accent, fontSize: 12, fontWeight: 700, mb: 0.75, letterSpacing: 0 }}>
              {eyebrow}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: { xs: 26, sm: 30 },
              lineHeight: 1.2,
              fontWeight: 650,
              letterSpacing: 0,
              color: adminTokens.ink,
              mb: description ? 0.75 : 0,
              overflowWrap: 'anywhere'
            }}
          >
            {title}
          </Typography>
          {description && <Typography sx={{ color: adminTokens.muted, fontSize: 14, lineHeight: 1.6 }}>{description}</Typography>}
        </Box>
        {actions}
      </Stack>
    </Box>
  );
}

export function AdminTableCard({ children }) {
  return (
    <Card
      sx={{
        overflow: 'hidden',
        '& .MuiTableCell-head': {
          bgcolor: adminTokens.surfaceAlt,
          color: '#53645E',
          fontSize: 13,
          fontWeight: 700,
          borderBottom: `1px solid ${adminTokens.border}`
        },
        '& .MuiTableCell-body': {
          borderBottom: `1px solid ${adminTokens.border}`,
          color: adminTokens.ink
        },
        '& .MuiTableRow-root:hover .MuiTableCell-body': {
          bgcolor: adminTokens.hover
        },
        '& .MuiTablePagination-root': {
          borderTop: `1px solid ${adminTokens.border}`,
          color: adminTokens.muted
        }
      }}
    >
      {children}
    </Card>
  );
}

AdminPage.propTypes = {
  children: PropTypes.node,
  spacing: PropTypes.number,
  sx: PropTypes.object
};

AdminPageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  actions: PropTypes.node,
  eyebrow: PropTypes.node,
  actionAlign: PropTypes.string
};

AdminTableCard.propTypes = {
  children: PropTypes.node
};
